import React, { useEffect, useState } from "react";
import { Delete, Eye, Pen } from "lucide-react";
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { createProduct, deleteProduct, getProducts, updateProduct } from "../../../slices/company/rawProductSlice";
import AddPurchases from "./add_purchases";
import { fetchRawCategories } from "../../../slices/company/rawCategorySlice";
import { fetchSuppliers } from "../../../slices/company/supplierSlice";
import { API_URL } from "../../../utils/constant";
import { createPurchase, deletePurchase, fetchPurchases, updatePurchase } from "../../../slices/company/purchasesSlice";
import { fetchDispenseMaterials } from "../../../slices/company/dispenseMaterialSlice";
import PurchasesProductForm from "./PurchasesProductForm";
import moment from "moment/moment";
import Select from "react-select";
import { customTableStyles } from "../../../utils/tableStylings";


const PurchasesProducts = () => {
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false); // To control modal visibility
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [modalHeading, setModalHeading] = useState('');
    const [viewOnly, setViewOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const { rawProducts } = useSelector(state => state.rawProduct);
    const { rawCategories } = useSelector(state => state.rawCategories);
    const { suppliers } = useSelector((state) => state.suppliers);
    const { purchases } = useSelector((state) => state.purchases);
    const { materials: dispenseMaterials } = useSelector((state) => state.dispenseMaterial || { materials: [] });

    const [filters, setFilters] = useState({
        supplier: '',
        bill_number: '',
        order_number: ''
    });

    const [appliedFilters, setAppliedFilters] = useState(filters);
    const supplierOptions = suppliers?.map((s) => ({
        value: s.id,
        label: s.name
    }));

    useEffect(() => {
        // setLoading(true);
        const company = JSON.parse(localStorage.getItem('company'));
        if (company) {
            dispatch(fetchSuppliers(company.company?.id));
            dispatch(fetchPurchases(company.company?.id));
            dispatch(fetchDispenseMaterials(company.company?.id));
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        const company = JSON.parse(localStorage.getItem('company'));
        if (company) {
            const companyId = company.company?.id;
            dispatch(fetchRawCategories(companyId));
            dispatch(getProducts(companyId));
        }
    }, [dispatch]);



    const handleFormSubmit = async (formData) => {
        const company = JSON.parse(localStorage.getItem('company'));
        const formattedFormData = {
            ...formData,
            bill_date: formData.bill_date ? new Date(formData.bill_date).toISOString().split('T')[0] : '',
            due_date: formData.due_date ? new Date(formData.due_date).toISOString().split('T')[0] : '',
            company_id: company.company?.id
        };

        let actionType = '';
        if (selectedProduct && selectedProduct.id != null) {
            await dispatch(updatePurchase({ id: selectedProduct.id, purchase: formattedFormData }));
            actionType = 'updated';
        } else {
            await dispatch(createPurchase(formattedFormData));
            actionType = 'created';
        }

        await dispatch(fetchPurchases(company.company?.id));

        setShowModal(false);
        Swal.fire({
            icon: 'success',
            title: `Purchase ${actionType}!`,
            text: `The purchase has been ${actionType} successfully.`,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };




    const handleEditTax = (productData) => {
        setSelectedProduct(productData);
        setModalHeading('Edit Purchases');
        setViewOnly(false);
        setShowModal(true);
    };

    const handleView = (productData) => {
        setSelectedProduct(productData);
        setModalHeading('View Purchases');
        setViewOnly(true);
        setShowModal(true);
    };

    const handleDelete = (taxId) => {
        Swal.fire({
            title: '<strong>Are you sure?</strong>',
            html: `<p>This action will <b>permanently delete</b> this Purchases.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deletePurchase(taxId));
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'The Purchases has been deleted.',
                    toast: true,          // Enable toast style
                    position: 'top-end',  // Position at the top-right corner
                    showConfirmButton: false,  // Hide the confirm button
                    timer: 3000,          // Set the timer to auto-close after 3 seconds
                    timerProgressBar: true,  // Show the progress bar for the timer
                    didOpen: (toast) => {
                        toast.addEventListener('mouseenter', Swal.stopTimer);
                        toast.addEventListener('mouseleave', Swal.resumeTimer);
                    }
                });

            }
        });

    };


    const productsColumn = [
        {
            name: '#',
            cell: (row, index) => index + 1, // Show index of the row
            width: '50px'
        },
        {
            name: 'Supplier Name',
            selector: row => row.Supplier?.name,
            sortable: true
        },
        {
            name: 'Bill Number',
            selector: row => row.bill_number,
            sortable: true
        },
        {
            name: 'Bill Date',
            selector: row => moment(row.bill_date).format('DD MMMM, YYYY'),
            sortable: true
        },
        {
            name: 'Order Number',
            selector: row => row.order_number,
            sortable: true
        },
        {
            name: 'Actions',
            cell: row => (
                <>
                    <span
                        className="text-info cursor-pointer me-2"
                        title="View"
                        onClick={() => handleView(row)}
                    >
                        <Eye />
                    </span>
                    <span
                        className="text-warning cursor-pointer me-2"
                        title="Edit"
                        onClick={() => handleEditTax(row)}
                    >
                        <Pen />
                    </span>
                    <span
                        className="text-danger cursor-pointer"
                        title="Delete"
                        onClick={() => handleDelete(row.id)}
                    >
                        <Delete />
                    </span>
                </>
            ),
            width: '220px'
        }
    ];

    const handleAdd = () => {
        setSelectedProduct(null);
        setModalHeading('Create New Purchase');// No supplier means we are adding a new one
        setShowModal(true);
    };


    const filteredPurchases = purchases?.filter((item) => {
        const supplierMatch =
            !appliedFilters.supplier ||
            item.vendor_id === appliedFilters.supplier.value;

        const billMatch =
            appliedFilters.bill_number === '' ||
            item.bill_number
                ?.toLowerCase()
                .includes(appliedFilters.bill_number.toLowerCase());

        const orderMatch =
            appliedFilters.order_number === '' ||
            item.order_number
                ?.toLowerCase()
                .includes(appliedFilters.order_number.toLowerCase());

        return supplierMatch && billMatch && orderMatch;
    });

    return (
        <>



            <div className="card" style={{ marginTop: "60px" }}>
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h3 className="text-primary mb-0">Purchases </h3>
                    <button
                        className="btn btn-primary"
                        onClick={() => handleAdd()}
                    >
                        + Add New   
                    </button>
                </div>
                <div className="card-header">
                    <div className="row mb-3 align-items-end">
                        <div className="col-md-3">
                            <div className="position-relative">

                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2" style={{ zIndex: "1" }}>
                                    Supplier
                                </span>
                                <Select
                                    options={supplierOptions}
                                    value={filters.supplier}
                                    onChange={(selected) =>
                                        setFilters({ ...filters, supplier: selected })
                                    }
                                    isClearable
                                    placeholder="Search Supplier"
                                />
                            </div>
                        </div>

                        <div className="col-md-3">
                            <div className="position-relative">

                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2" >
                                    Bill Number
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Bill Number"
                                    value={filters.bill_number}
                                    onChange={(e) =>
                                        setFilters({ ...filters, bill_number: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        <div className="col-md-3">
                            <div className="position-relative">

                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2" >
                                    Order Number
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Order Number"
                                    value={filters.order_number}
                                    onChange={(e) =>
                                        setFilters({ ...filters, order_number: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        <div className="col-md-3 d-flex gap-2">
                            <button
                                className="btn btn-success w-100"
                                onClick={() => setAppliedFilters(filters)}
                            >
                                Apply
                            </button>

                            <button
                                className="btn btn-outline-danger w-100"
                                onClick={() => {
                                    const reset = {
                                        supplier: null,
                                        bill_number: '',
                                        order_number: ''
                                    };
                                    setFilters(reset);
                                    setAppliedFilters(reset);
                                }}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    <DataTable
                        columns={productsColumn}
                        data={filteredPurchases}
                        pagination
                        highlightOnHover
                        striped
                        dense
                        customStyles={customTableStyles}
                    />
                </div>
            </div>

            <PurchasesProductForm show={showModal} onClose={() => {
                setShowModal(false);
                setSelectedProduct(null);
            }} heading={modalHeading} initialData={selectedProduct} onSubmit={handleFormSubmit} categories={rawCategories} suppliers={suppliers} rawProducts={rawProducts} dispenseMaterials={dispenseMaterials} viewOnly={viewOnly} />
        </>
    );
}

export default PurchasesProducts;